// ResearchSection.js
import React, { useState } from 'react';
import { 
  ChevronRight, 
  FlaskConical, 
  Microscope, 
  FileText, 
  Users, 
  Award,
  BookOpen,
  Calendar,
  Globe,
  TrendingUp,
  Lightbulb,
  Download,
  ExternalLink,
  GraduationCap,
  Building2,
  BadgeCheck,
  BookMarked,
  Presentation,
  PenTool,
  Leaf,
  Cpu,
  HeartPulse
} from 'lucide-react';

const ResearchSection = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const researchProjects = [
    {
      title: "AI in Healthcare: Predictive Diagnostics",
      department: "Computer Science",
      leadResearcher: "Dr. Priya Sharma",
      fundingAgency: "DST-SERB",
      budget: "₹45 Lakhs",
      duration: "2023-2025",
      status: "Ongoing",
      icon: Cpu
    },
    {
      title: "Sustainable Agriculture Practices",
      department: "Life Sciences",
      leadResearcher: "Dr. Amit Kumar",
      fundingAgency: "ICAR",
      budget: "₹32 Lakhs",
      duration: "2022-2024",
      status: "Ongoing",
      icon: Leaf
    },
    {
      title: "Drug Discovery for Tropical Diseases",
      department: "Pharmaceutical Sciences",
      leadResearcher: "Dr. Sneha Patel",
      fundingAgency: "DBT-BIRAC",
      budget: "₹78 Lakhs",
      duration: "2023-2026",
      status: "Ongoing",
      icon: HeartPulse
    },
    {
      title: "Renewable Energy Storage Solutions",
      department: "Physics",
      leadResearcher: "Dr. Rajesh Singh",
      fundingAgency: "MNRE",
      budget: "₹52 Lakhs",
      duration: "2022-2024",
      status: "Completed",
      icon: Lightbulb
    }
  ];

  const publications = [
    {
      title: "Machine Learning Approaches in Climate Prediction",
      authors: "Sharma P., Kumar A., et al.",
      journal: "Nature Climate Change",
      year: "2024",
      citations: 45,
      impact: "9.8",
      link: "#"
    },
    {
      title: "Novel Drug Delivery Systems for Cancer Therapy",
      authors: "Patel S., Singh R., et al.",
      journal: "Journal of Controlled Release",
      year: "2023",
      citations: 78,
      impact: "11.2",
      link: "#"
    },
    {
      title: "Quantum Computing: Recent Advances",
      authors: "Verma A., Sharma P., et al.",
      journal: "IEEE Transactions on Quantum Engineering",
      year: "2024",
      citations: 23,
      impact: "7.5",
      link: "#"
    },
    {
      title: "Sustainable Urban Development Models",
      authors: "Kumar A., Patel S., et al.",
      journal: "Urban Studies",
      year: "2023",
      citations: 56,
      impact: "8.3",
      link: "#"
    }
  ];

  const patents = [
    {
      title: "Smart Irrigation System Using IoT",
      inventors: "Dr. Amit Kumar, Dr. Priya Sharma",
      patentNo: "202311004567",
      date: "15 Jan 2024",
      status: "Granted"
    },
    {
      title: "Novel Compound for Diabetes Treatment",
      inventors: "Dr. Sneha Patel, Dr. Rajesh Singh",
      patentNo: "202311008923",
      date: "28 Feb 2024",
      status: "Filed"
    },
    {
      title: "AI-Based Crop Disease Detection",
      inventors: "Dr. Priya Sharma, Dr. Amit Kumar",
      patentNo: "202411001234",
      date: "10 Mar 2024",
      status: "Filed"
    }
  ];

  const collaborations = [
    {
      institution: "IIT Delhi",
      project: "AI Research Lab",
      duration: "2023-2026",
      type: "Academic"
    },
    {
      institution: "AIIMS Delhi",
      project: "Healthcare Analytics",
      duration: "2024-2027",
      type: "Medical"
    },
    {
      institution: "DRDO",
      project: "Defense Technologies",
      duration: "2023-2025",
      type: "Government"
    },
    {
      institution: "Microsoft Research",
      project: "Cloud Computing",
      duration: "2024-2026",
      type: "Industry"
    }
  ];

  const centers = [
    {
      name: "Centre for Artificial Intelligence",
      director: "Dr. Priya Sharma",
      focus: "Machine Learning, Deep Learning, NLP",
      scholars: 12,
      icon: Cpu
    },
    {
      name: "Centre for Biotechnology",
      director: "Dr. Amit Kumar",
      focus: "Genetic Engineering, Bioinformatics",
      scholars: 15,
      icon: Microscope
    },
    {
      name: "Centre for Sustainable Energy",
      director: "Dr. Rajesh Singh",
      focus: "Solar Energy, Battery Technology",
      scholars: 8,
      icon: Lightbulb
    },
    {
      name: "Centre for Pharmaceutical Research",
      director: "Dr. Sneha Patel",
      focus: "Drug Discovery, Formulation",
      scholars: 10,
      icon: FlaskConical
    }
  ];

  const stats = [
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-blue-900 to-blue-800 text-white py-16 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-orange-500 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-blue-400 rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-2 text-sm text-blue-200 mb-4">
            <span className="hover:text-orange-400 cursor-pointer">Home</span>
            <ChevronRight size={16} />
            <span className="text-orange-400 font-semibold">Research & Development</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Research & Development</h1>
          <p className="text-xl text-blue-100 max-w-2xl">Innovating for a better tomorrow through cutting-edge research</p>
        </div>
      </div>

      {/* Stats Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {stats.map((stat, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 text-center transform hover:scale-105 transition-all duration-300">
              <div className="inline-flex p-2 sm:p-3 bg-gradient-to-br from-orange-50 to-orange-100 rounded-full mb-2 sm:mb-3">
                <stat.icon className="w-4 h-4 sm:w-6 sm:h-6 text-orange-500" />
              </div>
              <div className="text-lg sm:text-xl md:text-2xl font-bold text-blue-900">{stat.number}</div>
              <div className="text-xs sm:text-sm text-gray-600">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 mt-12">
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full font-semibold text-sm sm:text-base transition-all duration-300 ${
              activeTab === 'overview'
                ? 'bg-orange-500 text-white shadow-lg'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('projects')}
            className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full font-semibold text-sm sm:text-base transition-all duration-300 ${
              activeTab === 'projects'
                ? 'bg-orange-500 text-white shadow-lg'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Research Projects
          </button>
          <button
            onClick={() => setActiveTab('publications')}
            className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full font-semibold text-sm sm:text-base transition-all duration-300 ${
              activeTab === 'publications'
                ? 'bg-orange-500 text-white shadow-lg'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Publications
          </button>
          <button
            onClick={() => setActiveTab('centers')}
            className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full font-semibold text-sm sm:text-base transition-all duration-300 ${
              activeTab === 'centers'
                ? 'bg-orange-500 text-white shadow-lg'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Research Centers
          </button>
          <button
            onClick={() => setActiveTab('collaborations')}
            className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full font-semibold text-sm sm:text-base transition-all duration-300 ${
              activeTab === 'collaborations'
                ? 'bg-orange-500 text-white shadow-lg'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Collaborations
          </button>
        </div>

        <div className="mt-8">
          {activeTab === 'overview' && (
            <div>
              <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
                <div>
                  <h2 className="text-3xl font-bold text-blue-900 mb-6">Driving Innovation Through Research</h2>
                  <p className="text-gray-600 mb-4">
                    At RDS College, research and development form the cornerstone of our academic excellence. We foster a culture of innovation, critical thinking, and scientific discovery that addresses real-world challenges.
                  </p>
                  <p className="text-gray-600 mb-6">
                    Our research ecosystem brings together brilliant minds from diverse disciplines, creating an interdisciplinary environment where groundbreaking ideas flourish. With state-of-the-art laboratories and collaborative partnerships, we're pushing the boundaries of knowledge.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gradient-to-br from-blue-50 to-orange-50 p-4 rounded-xl">
                      <div className="text-2xl font-bold text-blue-900">45+</div>
                      <div className="text-sm text-gray-600">Research Scholars</div>
                    </div>
                    <div className="bg-gradient-to-br from-blue-50 to-orange-50 p-4 rounded-xl">
                      <div className="text-2xl font-bold text-blue-900">25+</div>
                      <div className="text-sm text-gray-600">PhD Supervisors</div>
                    </div>
                  </div>
                </div>
                <div className="relative">
                  <div className="absolute -top-4 -left-4 w-24 h-24 bg-orange-100 rounded-full -z-10"></div>
                  <img 
                    src="image1.png" 
                    alt="Research Lab" 
                    className="rounded-2xl shadow-2xl w-full h-[300px] object-cover"
                  />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-blue-900 mb-6">Key Research Areas</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300">
                  <Cpu className="w-12 h-12 text-orange-500 mb-4" />
                  <h4 className="text-lg font-bold text-blue-900 mb-2">Artificial Intelligence</h4>
                  <p className="text-sm text-gray-600">Machine Learning, Deep Learning, NLP, Computer Vision</p>
                </div>
                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300">
                  <Microscope className="w-12 h-12 text-orange-500 mb-4" />
                  <h4 className="text-lg font-bold text-blue-900 mb-2">Biotechnology</h4>
                  <p className="text-sm text-gray-600">Genetic Engineering, Bioinformatics, Drug Discovery</p>
                </div>
                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300">
                  <Leaf className="w-12 h-12 text-orange-500 mb-4" />
                  <h4 className="text-lg font-bold text-blue-900 mb-2">Sustainable Energy</h4>
                  <p className="text-sm text-gray-600">Solar Energy, Battery Tech, Green Chemistry</p>
                </div>
                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300">
                  <HeartPulse className="w-12 h-12 text-orange-500 mb-4" />
                  <h4 className="text-lg font-bold text-blue-900 mb-2">Healthcare</h4>
                  <p className="text-sm text-gray-600">Medical Devices, Diagnostics, Therapeutics</p>
                </div>
              </div>
            </div>
          )}
          {activeTab === 'projects' && (
            <div>
              <h2 className="text-3xl font-bold text-center text-blue-900 mb-12">Ongoing Research Projects</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {researchProjects.map((project, index) => (
                  <div key={index} className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 border border-gray-100">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-400 rounded-xl flex items-center justify-center">
                        <project.icon className="w-6 h-6 text-white" />
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        project.status === 'Ongoing' ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'
                      }`}>
                        {project.status}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-blue-900 mb-2">{project.title}</h3>
                    <p className="text-sm text-gray-500 mb-4">{project.department}</p>
                    <div className="space-y-2 mb-4">
                      <p className="text-sm text-gray-600"><span className="font-semibold">Lead:</span> {project.leadResearcher}</p>
                      <p className="text-sm text-gray-600"><span className="font-semibold">Funding:</span> {project.fundingAgency} • {project.budget}</p>
                      <p className="text-sm text-gray-600"><span className="font-semibold">Duration:</span> {project.duration}</p>
                    </div>
                    <button className="text-orange-500 font-semibold flex items-center space-x-1 hover:space-x-2 transition-all duration-300">
                      <span>View Details</span>
                      <ExternalLink className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {activeTab === 'publications' && (
            <div>
              <h2 className="text-3xl font-bold text-center text-blue-900 mb-12">Recent Publications</h2>
              <div className="space-y-4">
                {publications.map((pub, index) => (
                  <div key={index} className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all duration-300">
                    <div className="flex flex-col md:flex-row md:items-center justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-blue-900 mb-2">{pub.title}</h3>
                        <p className="text-sm text-gray-600 mb-2">{pub.authors}</p>
                        <div className="flex flex-wrap items-center gap-4">
                          <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full">
                            {pub.journal}
                          </span>
                          <span className="text-xs text-gray-500">{pub.year}</span>
                          <span className="text-xs text-green-600">Citations: {pub.citations}</span>
                          <span className="text-xs text-orange-600">IF: {pub.impact}</span>
                        </div>
                      </div>
                      <a href={pub.link} className="mt-4 md:mt-0 text-orange-500 hover:text-orange-600">
                        <ExternalLink className="w-5 h-5" />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-center mt-8">
                <button className="inline-flex items-center space-x-2 bg-orange-500 text-white px-6 py-3 rounded-full font-semibold hover:bg-orange-600 transition-colors duration-300">
                  <Download className="w-4 h-4" />
                  <span>Download Publications List</span>
                </button>
              </div>
            </div>
          )}
          {activeTab === 'centers' && (
            <div>
              <h2 className="text-3xl font-bold text-center text-blue-900 mb-12">Research Centers</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {centers.map((center, index) => (
                  <div key={index} className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg p-6 border border-gray-100">
                    <div className="flex items-start space-x-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-blue-400 rounded-xl flex items-center justify-center flex-shrink-0">
                        <center.icon className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-blue-900 mb-2">{center.name}</h3>
                        <p className="text-sm text-gray-500 mb-2">Director: {center.director}</p>
                        <p className="text-sm text-gray-600 mb-3">{center.focus}</p>
                        <div className="flex items-center text-sm text-orange-500">
                          <GraduationCap className="w-4 h-4 mr-1" />
                          <span>{center.scholars} Research Scholars</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {activeTab === 'collaborations' && (
            <div>
              <h2 className="text-3xl font-bold text-center text-blue-900 mb-12">Research Collaborations</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {collaborations.map((collab, index) => (
                  <div key={index} className="bg-white rounded-xl shadow-md p-6 flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-blue-900 mb-1">{collab.institution}</h3>
                      <p className="text-sm text-gray-600 mb-2">{collab.project}</p>
                      <div className="flex items-center space-x-3">
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                          {collab.type}
                        </span>
                        <span className="text-xs text-gray-500">{collab.duration}</span>
                      </div>
                    </div>
                    <Building2 className="w-8 h-8 text-orange-500 opacity-50" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="bg-gradient-to-br from-blue-50 to-orange-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-blue-900 mb-4">Patents & IPR</h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Our research has led to several patents and intellectual property rights
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {patents.map((patent, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <Award className="w-8 h-8 text-orange-500" />
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    patent.status === 'Granted' ? 'bg-green-100 text-green-600' : 'bg-yellow-100 text-yellow-600'
                  }`}>
                    {patent.status}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-blue-900 mb-2">{patent.title}</h3>
                <p className="text-sm text-gray-600 mb-3">{patent.inventors}</p>
                <div className="text-xs text-gray-500">
                  <p>Patent No: {patent.patentNo}</p>
                  <p>Date: {patent.date}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Join Our Research Community</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Explore opportunities for PhD programs, research collaborations, and funding
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-orange-500 text-white px-8 py-3 rounded-full font-semibold hover:bg-orange-600 transition-colors duration-300">
              Apply for PhD
            </button>
            <button className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-blue-900 transition-colors duration-300">
              Propose Collaboration
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResearchSection;